import { getMPDataWithParams, postMPDataWithoutParams } from 'src/mentor/services'
import { getDataMapFromCommonResponse, isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'

// Owner-scoped Google Business config for a clinic brand. Backed by
// /api/secure/clinic-admin/google-business (ROLE_CLINIC_OWNER / ROLE_CLINIC_ADMIN + ownership check).
// Uses the generic mentor axios helpers — they hit the same host with the bearer token and the
// backend authorizes by role, so this works from both the mentor and clinic-owner portals.

function messageOf(response, fallback) {
  return response?.data?.message || response?.response?.data?.message || response?.message || fallback
}

export async function getGoogleBusinessConfig(clinicBrandId) {
  const response = await getMPDataWithParams('/api/secure/clinic-admin/google-business', { clinicBrandId })
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response) || {}
    return {
      ok: true,
      googleReviewUrl: data.googleReviewUrl || '',
      googlePlaceId: data.googlePlaceId || '',
    }
  }
  return { ok: false, message: messageOf(response, 'Unable to load Google Business settings.') }
}

export async function saveGoogleBusinessConfig(clinicBrandId, googleReviewUrl, googlePlaceId) {
  const response = await postMPDataWithoutParams('/api/secure/clinic-admin/google-business', {
    clinicBrandId,
    googleReviewUrl,
    googlePlaceId,
  })
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response) || {}
    return {
      ok: true,
      googleReviewUrl: data.googleReviewUrl || '',
      googlePlaceId: data.googlePlaceId || '',
    }
  }
  return { ok: false, message: messageOf(response, 'Unable to save Google Business settings.') }
}

// ---- Google Business Profile OAuth (Connect flow) ----

export async function getGoogleBusinessConnection(clinicBrandId) {
  const response = await getMPDataWithParams('/api/secure/clinic-admin/google-business/connection', {
    clinicBrandId,
  })
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response) || {}
    return { ok: true, connected: !!data.connected, googleAccountResourceName: data.googleAccountResourceName || '' }
  }
  return { ok: false, connected: false, message: messageOf(response, 'Unable to load connection status.') }
}

export async function connectGoogleBusiness(clinicBrandId, code, redirectUri) {
  const response = await postMPDataWithoutParams('/api/secure/clinic-admin/google-business/oauth/connect', {
    clinicBrandId,
    code,
    redirectUri,
  })
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return { ok: true }
  }
  return { ok: false, message: messageOf(response, 'Unable to connect Google Business.') }
}

export async function getGoogleBusinessLocations(clinicBrandId) {
  const response = await getMPDataWithParams('/api/secure/clinic-admin/google-business/locations', {
    clinicBrandId,
  })
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response) || {}
    return { ok: true, locations: data.locations || [] }
  }
  return { ok: false, locations: [], message: messageOf(response, 'Unable to load Google locations.') }
}

export async function selectGoogleBusinessLocation(clinicBrandId, locationName) {
  const response = await postMPDataWithoutParams('/api/secure/clinic-admin/google-business/select-location', {
    clinicBrandId,
    locationName,
  })
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    const data = getDataMapFromCommonResponse(response) || {}
    return { ok: true, googleReviewUrl: data.googleReviewUrl || '', googlePlaceId: data.googlePlaceId || '' }
  }
  return { ok: false, message: messageOf(response, 'Unable to save the selected location.') }
}

export async function disconnectGoogleBusiness(clinicBrandId) {
  const response = await postMPDataWithoutParams('/api/secure/clinic-admin/google-business/disconnect', {
    clinicBrandId,
  })
  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    return { ok: true }
  }
  return { ok: false, message: messageOf(response, 'Unable to disconnect.') }
}

// Google Business Profile OAuth client id — falls back to the Calendar client if a dedicated one
// isn't configured (that client must have the Business Profile API + business.manage scope).
export function getGoogleBusinessClientId() {
  return (
    import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID_BUSINESS ||
    import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID_CALENDAR ||
    ''
  )
}

export function getGoogleBusinessRedirectUri() {
  return `${window.location.origin}/oauth/google-business/callback`
}

export function buildGoogleBusinessAuthUrl(clinicBrandId) {
  const url = new URL('https://accounts.google.com/o/oauth2/v2/auth')
  url.searchParams.append('client_id', getGoogleBusinessClientId())
  url.searchParams.append('redirect_uri', getGoogleBusinessRedirectUri())
  url.searchParams.append('response_type', 'code')
  url.searchParams.append('scope', 'https://www.googleapis.com/auth/business.manage')
  url.searchParams.append('access_type', 'offline')
  url.searchParams.append('prompt', 'consent')
  url.searchParams.append('include_granted_scopes', 'true')
  url.searchParams.append('state', clinicBrandId || '')
  return url.toString()
}
